import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';

import { GlobalContext, Input, Button, Alert } from 'lynkco-up-core';
import { ReactInputEventHandler } from 'lynkco-up-core/dist/types/types';

import { Divider, VerificationScopeSelect } from '../../../components';
import { InputWrapper } from '../../shared';
import { getMaxLengthErrorMessage } from '../utils';
import { approveDriversLicense } from '../../../services/Applicants';
import { ApplicantQuery, VerificationScope, VerificationType } from '../../../types';
import { getUserSafeServiceError } from '../../../utils/serviceError';

const updateStatusErrorMessage =
  'We could not update the verification status with the details provided. Check the input and try again.';

enum ManualVerificationAction {
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
}

const manualVerificationStatusByAction = {
  [ManualVerificationAction.APPROVE]: 'FINISHED_SUCCESSFULLY_MANUALLY_UPDATED',
  [ManualVerificationAction.REJECT]: 'FINISHED_WITH_ERRORS_MANUALLY_UPDATED',
};

function ApproveChecksContent({ applicantQuery }: { applicantQuery: ApplicantQuery }) {
  const { alert, loading } = useContext(GlobalContext);
  const [customerNumber, setCustomerNumber] = useState(applicantQuery.customerNumber);
  const [caseId, setCaseId] = useState(applicantQuery.caseId);
  const [isCustomerSuggested, setIsCustomerSuggested] = useState(applicantQuery.customerNumber);
  const [reason, setReason] = useState('');
  const [verificationType, setVerificationType] = useState<VerificationType>(VerificationType.CAR_SHARING);
  const [action, setAction] = useState(ManualVerificationAction.APPROVE);
  const [formErrors, setFormErrors] = useState({
    customerNumber: false,
    caseId: false,
    reason: false,
  });
  const [resultError, setResultError] = useState<null | string>(null);
  const isSubmitDisabled = !customerNumber || !caseId || !reason || Object.values(formErrors).some(error => error);
  const inputRef = useRef<null | HTMLInputElement>(null);

  async function handleSubmit() {
    if (isSubmitDisabled) {
      return;
    }

    if (resultError) {
      setResultError(null);
    }

    loading.show();

    try {
      await approveDriversLicense(
        caseId,
        customerNumber,
        reason,
        verificationType,
        manualVerificationStatusByAction[action],
      );
      alert.show(`Verification ${action === ManualVerificationAction.APPROVE ? 'approved' : 'rejected'}.`, 'success');
    } catch (error) {
      setResultError(getUserSafeServiceError(error, updateStatusErrorMessage));
    }

    loading.hide();
  }

  function handleDismissCustomerSuggestion() {
    setIsCustomerSuggested('');
  }

  const handleChangeCustomerNumber: ReactInputEventHandler = e => {
    setCustomerNumber(e.currentTarget.value);
  };

  const handleChangeCaseId: ReactInputEventHandler = e => {
    setCaseId(e.currentTarget.value);
  };

  const handleChangeReason: ReactInputEventHandler = e => {
    setReason(e.currentTarget.value);
  };

  const handleChangeAction = (e: ChangeEvent<HTMLSelectElement>) => {
    setAction(e.currentTarget.value as ManualVerificationAction);
  };

  function handleChangeVerificationType(value: VerificationScope) {
    setVerificationType(value as VerificationType);
  }

  function handleDismissResultError() {
    setResultError(null);
  }

  useEffect(() => {
    setFormErrors({
      customerNumber: customerNumber.length > 50,
      caseId: caseId.length > 50,
      reason: reason.length > 150,
    });
  }, [customerNumber, caseId, reason]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div>
      <h2 className="font-medium text-xl mb-7">Update verification status</h2>
      {resultError && (
        <Alert
          type="error"
          heading="There was an error while trying to manually update verification status"
          body={`${resultError}`}
          onDismiss={handleDismissResultError}
        />
      )}
      {isCustomerSuggested && (
        <Alert
          type="success"
          heading="Suggested customer"
          body={`We selected customer ${applicantQuery.customerNumber} for you`}
          onDismiss={handleDismissCustomerSuggestion}
        />
      )}
      <Divider />
      <InputWrapper label="Customer number*">
        <Input
          type="text"
          extraClasses="basis-2/3 -mt-1"
          onChange={handleChangeCustomerNumber}
          errorText={getMaxLengthErrorMessage(50)}
          hasError={formErrors.customerNumber}
          ref={inputRef}
          value={customerNumber}
        />
      </InputWrapper>
      <Divider />
      <InputWrapper label="Case ID*">
        <Input
          extraClasses="basis-2/3 -mt-1"
          onChange={handleChangeCaseId}
          errorText={getMaxLengthErrorMessage(50)}
          hasError={formErrors.caseId}
          value={caseId}
        />
      </InputWrapper>
      <Divider />
      <InputWrapper label="Verification type*">
        <VerificationScopeSelect value={verificationType} onChange={handleChangeVerificationType} includeAll={false} />
      </InputWrapper>
      <Divider />
      <InputWrapper label="Action*">
        <select
          className="basis-2/3 -mt-1 border border-gray-300 rounded px-3 py-2 bg-white"
          value={action}
          onChange={handleChangeAction}>
          <option value={ManualVerificationAction.APPROVE}>Approve</option>
          <option value={ManualVerificationAction.REJECT}>Reject</option>
        </select>
      </InputWrapper>
      <Divider />
      <InputWrapper label="Reason*">
        <Input
          extraClasses="basis-2/3 -mt-1"
          onChange={handleChangeReason}
          errorText={getMaxLengthErrorMessage(150)}
          hasError={formErrors.reason}
          value={reason}
        />
      </InputWrapper>
      <Divider />
      <div className="flex justify-end mt-7">
        <Button onClick={() => handleSubmit()} isDisabled={isSubmitDisabled}>
          Submit
        </Button>
      </div>
    </div>
  );
}

export default ApproveChecksContent;
